import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Handshake, EnvelopeSimple } from "@phosphor-icons/react";
import distApi from "@/lib/distApi";
import { detectLang, initI18n, t } from "@/lib/i18n";
import { errDetail } from "@/lib/api";

export default function DistributorRegister() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("register");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [accept, setAccept] = useState(false);
  const [otpStep, setOtpStep] = useState(false);
  const [code, setCode] = useState("");
  const [demoOtp, setDemoOtp] = useState(null);
  const [busy, setBusy] = useState(false);
  const [brandName, setBrandName] = useState("Agentic Inbox");
  const [, setI18nReady] = useState(false);

  useEffect(() => {
    initI18n().then(() => setI18nReady(true));
    distApi.get("/site/landing").then((r) => setBrandName(r.data?.brand?.app_name || "Agentic Inbox")).catch(() => {});
  }, []);

  const register = async () => {
    setBusy(true);
    try {
      const { data } = await distApi.post("/distributors/register", { name, email, accept_terms: accept, ui_lang: detectLang() });
      setDemoOtp(data.demo_otp || null);
      setOtpStep(true);
      toast.success(t("dist.created_toast", "Account created — enter the code we sent you"));
    } catch (e) { toast.error(errDetail(e)); }
    finally { setBusy(false); }
  };

  const requestOtp = async () => {
    setBusy(true);
    try {
      const { data } = await distApi.post("/distributors/request-otp", { email });
      setDemoOtp(data.demo_otp || null);
      setOtpStep(true);
    } catch (e) { toast.error(errDetail(e)); }
    finally { setBusy(false); }
  };

  const verify = async () => {
    setBusy(true);
    try {
      const { data } = await distApi.post("/distributors/verify-otp", { email, code });
      localStorage.setItem("agi_dist_token", data.token);
      toast.success(t("dist.welcome", "Welcome, {name}!", { name: data.distributor.name }));
      navigate("/distributor");
    } catch (e) { toast.error(errDetail(e)); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="distributor-register-page" className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="w-full max-w-md bg-white border border-gray-200 rounded-lg shadow-sm p-8">
        <div className="flex items-center gap-2 mb-1">
          <Handshake size={22} className="text-blue-600" />
          <h1 className="font-heading text-xl font-bold text-gray-900">{t("dist.program", "Distributor Program")}</h1>
        </div>
        <p className="text-sm text-gray-500 mb-6">{t("dist.tagline", "Help us sell {brand} and earn a commission on every payment your referrals make.", { brand: brandName })}</p>

        {!otpStep ? (
          <>
            <div className="flex gap-2 mb-5">
              <button data-testid="dist-reg-tab" onClick={() => setTab("register")}
                className={`flex-1 text-sm font-semibold rounded-md py-2 ${tab === "register" ? "bg-blue-600 text-white" : "bg-gray-100 text-gray-600"}`}>{t("dist.tab_register", "Register")}</button>
              <button data-testid="dist-login-tab" onClick={() => setTab("login")}
                className={`flex-1 text-sm font-semibold rounded-md py-2 ${tab === "login" ? "bg-blue-600 text-white" : "bg-gray-100 text-gray-600"}`}>{t("dist.tab_login", "Log in")}</button>
            </div>

            {tab === "register" ? (
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">{t("dist.your_name", "Your name")}</label>
                  <input data-testid="dist-reg-name" value={name} onChange={(e) => setName(e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" placeholder="Maria García" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">{t("dist.email", "Email")}</label>
                  <input data-testid="dist-reg-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" placeholder="you@example.com" />
                </div>
                <label className="flex items-start gap-2 text-xs text-gray-600 cursor-pointer">
                  <input data-testid="dist-reg-terms-checkbox" type="checkbox" checked={accept} onChange={(e) => setAccept(e.target.checked)}
                    className="mt-0.5 accent-blue-600" />
                  <span>
                    {t("dist.accept_pre", "I accept the")}{" "}
                    <Link data-testid="dist-reg-terms-link" to="/distributors/terms" target="_blank" className="text-blue-600 underline">{t("dist.terms_link", "Distributor Terms & Conditions")}</Link>
                    {" "}{t("dist.and_the", "and the")}{" "}
                    <Link data-testid="dist-reg-privacy-link" to="/distributors/privacy" target="_blank" className="text-blue-600 underline">{t("dist.privacy_link", "Distributor Privacy Policy")}</Link>
                  </span>
                </label>
                <button data-testid="dist-reg-submit" onClick={register} disabled={busy || !accept || !name.trim() || !email.trim()}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-md py-2.5 disabled:opacity-50">
                  {busy ? t("dist.creating", "Creating…") : t("dist.become", "Become a distributor")}
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">{t("dist.email", "Email")}</label>
                  <input data-testid="dist-login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" placeholder="you@example.com" />
                </div>
                <button data-testid="dist-login-submit" onClick={requestOtp} disabled={busy || !email.trim()}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-md py-2.5 disabled:opacity-50">
                  {busy ? t("dist.sending", "Sending…") : t("dist.send_code", "Send login code")}
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-sm text-gray-600"><EnvelopeSimple size={16} /> {t("dist.enter_code", "Enter the 6-digit code for")} <b>{email}</b></div>
            {demoOtp && <p data-testid="dist-demo-otp" className="text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded px-3 py-2">{t("dist.demo_code", "Demo mode — your code:")} <b className="font-mono">{demoOtp}</b></p>}
            <input data-testid="dist-otp-input" value={code} onChange={(e) => setCode(e.target.value)} maxLength={6}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-center text-lg tracking-[8px] font-mono" placeholder="••••••" />
            <button data-testid="dist-otp-verify" onClick={verify} disabled={busy || code.length !== 6}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-md py-2.5 disabled:opacity-50">
              {busy ? t("dist.verifying", "Verifying…") : t("dist.verify_btn", "Verify & enter panel")}
            </button>
            <button data-testid="dist-otp-back" onClick={() => { setOtpStep(false); setCode(""); }} className="w-full text-xs text-gray-400 hover:text-gray-600">{t("dist.back", "← Back")}</button>
          </div>
        )}

        <p className="text-[11px] text-gray-400 mt-6 text-center">{t("dist.looking", "Looking for the email app?")} <Link to="/" className="text-blue-600 underline">{t("dist.goto", "Go to {brand}", { brand: brandName })}</Link></p>
      </div>
    </div>
  );
}
